/**
 * CoordenadasSection — sección Coordenadas de la ficha (P2RED S1-3b).
 *
 * Contenido de la ranura `secciones.coordenadas` de `FichaPanel`:
 * - Con coordenada: X/Y mono + subtítulo de origen (original inmutable, §6) +
 *   fila de score agregado con barra (RED-1.coords=iii: SOLO el agregado; el
 *   desglose D1/D2/D3 vive en traza/exportación) + botonera.
 * - Sin coordenada: mensaje accionable + primario «Pegar coordenada».
 *
 * Botonera (decisión M2): primario «Pegar coordenada» + secundarios icono
 * (copiar «X, Y» · Google Maps con el pin multicolor del P1 vivo · visor DERA).
 * S1-4a (sustituye al interino DOP-S1-3b-1=a): el primario despliega el
 * conversor multiformato IN-FICHA (DMS/DD/UTM → UTM30 vía parseCoordinateInput
 * + toUtmInZone, mismo camino que CoordinateFields) y aplica por el canal C2
 * (`onAplicar`, editSource 'manual' aguas arriba). El handle imperativo
 * `pegarTexto` permite a Step2Page enrutar ⌘V al mismo conversor.
 * ED50: NUNCA se transforma automáticamente — heurística textual ⇒ solo aviso
 * (la transformación asistida llega en S1-6).
 *
 * Sin fetch; estado local solo del conversor. Skin DS v2, sin glow (regla dura).
 *
 * @version 2.0.0 (S1-4a)
 */

import {
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FC,
  type KeyboardEvent,
  type Ref,
} from 'react';
import { colors, typography, spacing } from '@/design-system/v2';
import { generateLocationURLs } from '@/lib/generators/LocationURLGenerator';
import { PinGoogleMaps } from '../step1-normaliza/NormalizationTable';
import { parseCoordinateInput, type ParsedCoordinate } from '@/utils/coordinateParser';
import { toUtmInZone, ANDALUCIA_BOUNDS_UTM30, type UtmCoordinate } from '@/utils/projections';
import { preProcessCoordinateValue } from '@/pipeline/integration';

// ============================================================================
// TYPES
// ============================================================================

/** Handle imperativo del conversor (⌘V enrutado desde Step2Page, S1-4a). */
export interface CoordenadasSectionHandle {
  /** Abre el conversor in-ficha con el texto pegado ya cargado. */
  pegarTexto: (texto: string) => void;
}

/** Resultado del análisis del texto pegado (derivado, sin estado propio). */
interface AnalisisPegado {
  parsed: ParsedCoordinate | null;
  utm30: UtmCoordinate | null;
  enRango: boolean;
  avisoED50: boolean;
  error: string | null;
}

export interface CoordenadasSectionProps {
  /** Coordenada final UTM30 (EPSG:25830); null/undefined = sin ubicar. */
  coordenada?: { x: number; y: number } | null;
  /** Score agregado 0-100 del elemento (RED-1.coords=iii). */
  score?: number;
  /** Fuente de la coordenada final (subtítulo de origen). */
  fuente?: string;
  /**
   * CENFOR-B · aviso del elemento forzado al centroide municipal (mismo texto
   * que `MOTIVO`/`OBSERVACIONES` del entregable, `avisoCentroide`). Lo deriva
   * el padre (DOP-CENFORB-1=b); la ficha solo lo pinta cuando llega.
   */
  aviso?: string;
  /**
   * Aplica una coordenada UTM30 por el canal C2 (editSource 'manual' aguas
   * arriba). Ausente ⇒ solo lectura (rol viewer, candado FB.10.17): sin
   * primario y con el conversor (incluido ⌘V) inerte.
   */
  onAplicar?: (x: number, y: number) => void;
  /**
   * Arma/desarma el modo «Ubicar en el mapa» (S1-4b): un clic en el lienzo
   * fija la coordenada por el MISMO canal C2 del arrastre (editSource 'map'
   * aguas arriba). Ausente ⇒ el botón no se muestra (rol viewer o sin canal).
   */
  onUbicar?: () => void;
  /** true mientras el modo «Ubicar en el mapa» está armado (feedback del botón). */
  ubicarActivo?: boolean;
  /** Handle imperativo para enrutar ⌘V al conversor (S1-4a). */
  ref?: Ref<CoordenadasSectionHandle>;
}

// ============================================================================
// STYLES (DS v2 · sin glow)
// ============================================================================

const coordValStyle: CSSProperties = {
  margin: 0,
  fontFamily: typography.fontFamily.mono,
  fontSize: typography.fontSize.sm,
  color: colors.fg.primary,
};

const coordSubStyle: CSSProperties = {
  margin: `${spacing[1]} 0 0`,
  fontSize: typography.fontSize.xs,
  color: colors.fg.muted,
};

/** CENFOR-B · aviso del centroide forzado bajo la coordenada (ámbar del DS, sin glow). */
const coordAvisoStyle: CSSProperties = {
  margin: `${spacing[1]} 0 0`,
  fontSize: typography.fontSize.xs,
  color: colors.accent.amberLight,
};

const scoreRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: spacing[2],
  marginTop: spacing[2],
  fontSize: typography.fontSize.xs,
  color: colors.fg.muted,
};

const scoreBarStyle: CSSProperties = {
  flex: 1,
  height: '4px',
  borderRadius: '2px',
  background: colors.bg.tertiary,
  overflow: 'hidden',
};

const scoreNumStyle: CSSProperties = {
  fontFamily: typography.fontFamily.mono,
  fontWeight: 600,
  color: colors.fg.primary,
};

const botoneraStyle: CSSProperties = {
  display: 'flex',
  gap: spacing[2],
  marginTop: spacing[3],
  alignItems: 'stretch',
};

const btnPrimarioStyle: CSSProperties = {
  flex: 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  background: colors.primary,
  color: colors.bg.primary,
  fontFamily: typography.fontFamily.sans,
  fontWeight: 600,
  border: 'none',
  borderRadius: '7px',
  padding: `7px ${spacing[2]}`,
  fontSize: typography.fontSize.xs,
  cursor: 'pointer',
};

const btnIconoStyle: CSSProperties = {
  flex: '0 0 auto',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  background: 'transparent',
  border: `1px solid ${colors.border.DEFAULT}`,
  color: colors.fg.muted,
  borderRadius: '7px',
  cursor: 'pointer',
};

// S1-4b · botón «Ubicar en el mapa»: secundario neutro; armado = borde/texto
// primario (constancia visual en la propia ficha, sin glow).
const btnUbicarStyle: CSSProperties = {
  flex: 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  background: 'transparent',
  color: colors.fg.primary,
  fontFamily: typography.fontFamily.sans,
  fontWeight: 600,
  border: `1px solid ${colors.border.DEFAULT}`,
  borderRadius: '7px',
  padding: `7px ${spacing[2]}`,
  fontSize: typography.fontSize.xs,
  cursor: 'pointer',
};

const btnUbicarActivoStyle: CSSProperties = {
  ...btnUbicarStyle,
  // Shorthand completo (no `borderColor`): mezclar shorthand y propiedad suelta
  // en el MISMO valor provoca el warning de React al alternar armado/desarmado.
  border: `1px solid ${colors.primary}`,
  color: colors.primary,
};

const sinCoordStyle: CSSProperties = {
  margin: 0,
  fontSize: typography.fontSize.xs,
  color: colors.error,
};

// --- Conversor pegar-multiformato (S1-4a) ---

const conversorWrapStyle: CSSProperties = {
  marginTop: spacing[2],
  display: 'flex',
  flexDirection: 'column',
  gap: spacing[1],
};

const conversorInputStyle: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  background: colors.bg.tertiary,
  border: `1px solid ${colors.border.DEFAULT}`,
  borderRadius: '7px',
  padding: `6px ${spacing[2]}`,
  fontFamily: typography.fontFamily.mono,
  fontSize: typography.fontSize.xs,
  color: colors.fg.primary,
};

const previewStyle: CSSProperties = {
  margin: 0,
  fontFamily: typography.fontFamily.mono,
  fontSize: typography.fontSize.xs,
  color: colors.fg.primary,
};

const errorTextStyle: CSSProperties = {
  margin: 0,
  fontSize: typography.fontSize.xs,
  color: colors.error,
};

const warnTextStyle: CSSProperties = {
  margin: 0,
  fontSize: typography.fontSize.xs,
  color: colors.warning,
};

const conversorBotoneraStyle: CSSProperties = {
  display: 'flex',
  gap: spacing[2],
  marginTop: spacing[1],
};

const btnSecundarioStyle: CSSProperties = {
  flex: '0 0 auto',
  background: 'transparent',
  border: `1px solid ${colors.border.DEFAULT}`,
  color: colors.fg.muted,
  fontFamily: typography.fontFamily.sans,
  borderRadius: '7px',
  padding: `5px ${spacing[2]}`,
  fontSize: typography.fontSize.xs,
  cursor: 'pointer',
};

// ============================================================================
// HELPERS
// ============================================================================

/** Metro entero como tabla/shell; 2 decimales solo con precisión submétrica. */
const fmt = (v: number): string => (Number.isInteger(v) ? String(v) : v.toFixed(2));

/** Formato de copia coherente con el copiar-en-fila de la tabla (S1-2). */
const clipboardXY = (x: number, y: number): string => `${x.toFixed(2)}, ${y.toFixed(2)}`;

const IconoCopiar: FC = () => (
  <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const IconoMapa: FC = () => (
  <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
    <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" />
    <line x1="9" y1="3" x2="9" y2="18" />
    <line x1="15" y1="6" x2="15" y2="21" />
  </svg>
);

const IconoPegar: FC = () => (
  <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" />
  </svg>
);

// S1-4b · cruceta (lucide `crosshair`)
const IconoCruceta: FC = () => (
  <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
    <circle cx="12" cy="12" r="7" />
    <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
  </svg>
);

// ============================================================================
// COMPONENT
// ============================================================================

/** Analiza el texto pegado: limpieza (pipeline FMT-03) → parse multiformato → UTM30. */
function analizarTexto(texto: string): AnalisisPegado | null {
  const limpio = texto.trim();
  if (!limpio) return null;
  // ED50: heurística textual — el parser no expone datum. NUNCA transformar (S1-6).
  const avisoED50 = /ED\s*-?\s*50/i.test(limpio);
  const pre = preProcessCoordinateValue(limpio, 'plain');
  const parsed = parseCoordinateInput(pre.cleaned);
  if (parsed == null) {
    return { parsed: null, utm30: null, enRango: false, avisoED50, error: 'Formato no reconocido (acepta DMS, DD o UTM)' };
  }
  let utm30: UtmCoordinate | null = null;
  try {
    utm30 = toUtmInZone(parsed.lat, parsed.lon, 'EPSG:25830');
  } catch {
    utm30 = null;
  }
  if (utm30 == null) {
    return { parsed, utm30: null, enRango: false, avisoED50, error: 'No se pudo convertir a UTM30' };
  }
  const enRango =
    utm30.x >= ANDALUCIA_BOUNDS_UTM30.minX && utm30.x <= ANDALUCIA_BOUNDS_UTM30.maxX &&
    utm30.y >= ANDALUCIA_BOUNDS_UTM30.minY && utm30.y <= ANDALUCIA_BOUNDS_UTM30.maxY;
  return { parsed, utm30, enRango, avisoED50, error: enRango ? null : 'Coordenadas fuera del rango de Andalucía' };
}

export const CoordenadasSection: FC<CoordenadasSectionProps> = ({
  coordenada,
  score,
  fuente,
  aviso,
  onAplicar,
  onUbicar,
  ubicarActivo = false,
  ref,
}) => {
  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const analisis = useMemo(() => analizarTexto(texto), [texto]);

  const cerrar = useCallback(() => {
    setAbierto(false);
    setTexto('');
  }, []);

  const pegarTexto = useCallback((t: string) => {
    if (onAplicar == null) return; // solo-lectura (FB.10.17): conversor inerte
    setAbierto(true);
    setTexto(t);
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [onAplicar]);

  useImperativeHandle(ref, () => ({ pegarTexto }), [pegarTexto]);

  const puedeAplicar = onAplicar != null && analisis?.utm30 != null && analisis.enRango;

  const aplicar = useCallback(() => {
    if (onAplicar == null || analisis?.utm30 == null || !analisis.enRango) return;
    onAplicar(Number(analisis.utm30.x.toFixed(2)), Number(analisis.utm30.y.toFixed(2)));
    cerrar();
  }, [onAplicar, analisis, cerrar]);

  const onInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); aplicar(); }
    if (e.key === 'Escape') { e.preventDefault(); cerrar(); }
  };

  const btnPegar = onAplicar != null && (
    <button
      type="button"
      style={btnPrimarioStyle}
      onClick={() => { if (abierto) { cerrar(); } else { pegarTexto(''); } }}
      data-testid="coord-btn-pegar"
    >
      <IconoPegar />
      Pegar coordenada
    </button>
  );

  // S1-4b · armar/desarmar el modo «Ubicar en el mapa» (mismo canal C2 'map').
  const btnUbicar = onUbicar != null && (
    <button
      type="button"
      style={ubicarActivo ? btnUbicarActivoStyle : btnUbicarStyle}
      onClick={onUbicar}
      aria-pressed={ubicarActivo}
      title="Ubicar en el mapa: un clic en el lienzo fija la coordenada (Esc cancela)"
      data-testid="coord-btn-ubicar"
    >
      <IconoCruceta />
      {ubicarActivo ? 'Ubicando… (Esc cancela)' : 'Ubicar en el mapa'}
    </button>
  );

  const conversor = abierto && onAplicar != null && (
    <div style={conversorWrapStyle} data-testid="coord-conversor">
      <input
        ref={inputRef}
        type="text"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        onKeyDown={onInputKeyDown}
        placeholder={'Ej: 37°11\'03.5"N 3°36\'21.5"W · 447850, 4116234'}
        aria-label="Pegar coordenada (DMS, DD o UTM)"
        style={conversorInputStyle}
        data-testid="coord-pegar-input"
      />
      {analisis?.utm30 != null && (
        <p style={previewStyle} data-testid="coord-pegar-preview">
          → X {analisis.utm30.x.toFixed(2)} · Y {analisis.utm30.y.toFixed(2)} · EPSG:25830
        </p>
      )}
      {analisis?.error != null && (
        <p style={errorTextStyle} data-testid="coord-pegar-error">{analisis.error}</p>
      )}
      {analisis?.avisoED50 === true && (
        <p style={warnTextStyle} data-testid="coord-aviso-ed50">
          ED50 detectado — no se transforma automáticamente; verifica el datum.
        </p>
      )}
      <div style={conversorBotoneraStyle}>
        <button
          type="button"
          style={puedeAplicar ? { ...btnPrimarioStyle, flex: '0 0 auto' } : { ...btnPrimarioStyle, flex: '0 0 auto', opacity: 0.4, cursor: 'not-allowed' }}
          onClick={aplicar}
          disabled={!puedeAplicar}
          data-testid="coord-pegar-aplicar"
        >
          Aplicar
        </button>
        <button type="button" style={btnSecundarioStyle} onClick={cerrar} data-testid="coord-pegar-cancelar">
          Cancelar
        </button>
      </div>
    </div>
  );

  if (coordenada == null) {
    return (
      <div data-testid="coordenadas-seccion">
        <p style={sinCoordStyle} data-testid="coord-sin">
          Sin coordenada — pégala desde Google Maps/DMS/UTM.
        </p>
        {(btnPegar !== false || btnUbicar !== false) && (
          <div style={botoneraStyle}>
            {btnPegar}
            {btnUbicar}
          </div>
        )}
        {conversor}
      </div>
    );
  }

  const { x, y } = coordenada;
  const urls = generateLocationURLs({ x, y });

  const copiar = () => {
    void navigator.clipboard?.writeText(clipboardXY(x, y));
  };

  return (
    <div data-testid="coordenadas-seccion">
      <p style={coordValStyle} data-testid="coord-valor">
        X {fmt(x)} · Y {fmt(y)}
        <span style={{ color: colors.fg.muted }}> · EPSG:25830</span>
      </p>
      {fuente && (
        <p style={coordSubStyle}>
          {fuente} · original inmutable (§6)
        </p>
      )}
      {aviso && (
        <p style={coordAvisoStyle} role="note" data-testid="coord-aviso">
          {aviso}
        </p>
      )}
      {score != null && (
        <div style={scoreRowStyle} data-testid="coord-score">
          <span>Score</span>
          <span style={scoreBarStyle} aria-hidden="true">
            <span
              style={{
                display: 'block',
                height: '100%',
                width: `${Math.max(0, Math.min(100, score))}%`,
                background: colors.success,
              }}
            />
          </span>
          <span style={scoreNumStyle}>{score}</span>
        </div>
      )}
      <div style={botoneraStyle}>
        {btnPegar}
        {btnUbicar}
        <button
          type="button"
          style={btnIconoStyle}
          onClick={copiar}
          title="Copiar X, Y al portapapeles"
          aria-label="Copiar coordenadas al portapapeles"
          data-testid="coord-btn-copiar"
        >
          <IconoCopiar />
        </button>
        <a
          href={urls.googleMaps}
          target="_blank"
          rel="noopener noreferrer"
          style={btnIconoStyle}
          title="Abrir en Google Maps"
          aria-label="Abrir en Google Maps"
          data-testid="coord-btn-google"
        >
          <PinGoogleMaps />
        </a>
        <a
          href={urls.deraViewer}
          target="_blank"
          rel="noopener noreferrer"
          style={btnIconoStyle}
          title="Abrir en visor DERA (Junta de Andalucía)"
          aria-label="Abrir en visor DERA"
          data-testid="coord-btn-dera"
        >
          <IconoMapa />
        </a>
      </div>
      {conversor}
    </div>
  );
};

export default CoordenadasSection;
